"use client";

import { useEffect, useMemo, useState, useCallback, DragEvent } from "react";
import { leadStore, LeadItem, LeadNote, LeadStatus } from "@/lib/store";
import { Plus, X, Trash2, Star, GripVertical } from "lucide-react";

const STATUSES: { value: LeadStatus; label: string; color: string }[] = [
  { value: "new", label: "New", color: "#a1a1aa" },
  { value: "contacted", label: "Contacted", color: "#3b82f6" },
  { value: "followup", label: "Follow-up", color: "#06b6d4" },
  { value: "responded", label: "Responded", color: "#eab308" },
  { value: "meeting_booked", label: "Meeting Booked", color: "#a855f7" },
  { value: "proposal_sent", label: "Proposal Sent", color: "#f97316" },
  { value: "hold", label: "Hold", color: "#78716c" },
  { value: "won", label: "Won", color: "#22c55e" },
  { value: "lost", label: "Lost", color: "#ef4444" },
];

const emptyLead: Omit<LeadItem, "id" | "createdAt" | "updatedAt"> = {
  name: "", title: "", company: "", website: "", linkedin: "", email: "",
  phone: "", city: "", state: "", status: "new", notes: "", lastContacted: "",
};

export function LeadsKanban() {
  const [leads, setLeads] = useState<LeadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<LeadItem | null>(null);
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [newNote, setNewNote] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyLead);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    const rows = await leadStore.list();
    setLeads(rows);
    setLoading(false);
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const grouped = useMemo(() => {
    const map: Record<string, LeadItem[]> = {};
    for (const s of STATUSES) map[s.value] = [];
    for (const l of leads) {
      if (map[l.status]) map[l.status].push(l);
      else map["new"].push(l);
    }
    return map;
  }, [leads]);

  const onDragStart = (e: DragEvent, lead: LeadItem) => {
    e.dataTransfer.setData("text/plain", String(lead.id));
    e.dataTransfer.effectAllowed = "move";
    setDraggingId(lead.id);
  };

  const onDragEnd = () => {
    setDraggingId(null);
    setDragOverCol(null);
  };

  const onDrop = async (e: DragEvent, status: LeadStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    setDraggingId(null);
    const id = Number(e.dataTransfer.getData("text/plain"));
    if (!id) return;
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.status === status) return;
    // Optimistic update
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    await leadStore.update(id, { status });
  };

  const openEdit = async (lead: LeadItem) => {
    setEditing({ ...lead });
    const rows = await leadStore.notes(lead.id);
    setNotes(rows);
  };

  const saveEdit = async () => {
    if (!editing) return;
    await leadStore.update(editing.id, editing);
    setEditing(null);
    await loadLeads();
  };

  const addNote = async () => {
    if (!editing || !newNote.trim()) return;
    await leadStore.addNote(editing.id, newNote.trim());
    setNewNote("");
    setNotes(await leadStore.notes(editing.id));
  };

  const createLead = async () => {
    if (!createForm.name.trim() && !createForm.company.trim()) return;
    await leadStore.create(createForm);
    setCreateForm(emptyLead);
    setShowCreate(false);
    await loadLeads();
  };

  const importanceStars = (notes: string) => {
    const match = notes?.match(/importance:\s*(\d)/i);
    const count = match ? Math.min(5, Math.max(0, Number(match[1]))) : 0;
    if (!count) return null;
    return (
      <div className="flex gap-0.5">
        {Array.from({ length: count }).map((_, i) => (
          <Star key={i} size={12} fill="#eab308" stroke="#eab308" />
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[var(--text-muted)]">{leads.length} leads</div>
        <button onClick={() => setShowCreate(true)} className="px-3 py-2 rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-sm flex items-center gap-2">
          <Plus size={16} />Add Lead
        </button>
      </div>

      {loading ? (
        <div className="text-center text-[var(--text-muted)] py-12">Loading leads...</div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: "calc(100vh - 220px)" }}>
          {STATUSES.map((s) => (
            <div
              key={s.value}
              className="flex-shrink-0 flex flex-col rounded-xl border border-[var(--border)] bg-[var(--bg-card)]"
              style={{ width: 280, borderTopColor: s.color, borderTopWidth: 3 }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverCol(s.value); }}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={(e) => onDrop(e, s.value)}
            >
              <div className="px-3 py-2 flex items-center justify-between border-b border-[var(--border)]">
                <span className="text-sm font-semibold" style={{ color: s.color }}>{s.label}</span>
                <span className="text-xs text-[var(--text-muted)] bg-[var(--bg-column)] px-2 py-0.5 rounded-full">
                  {grouped[s.value]?.length || 0}
                </span>
              </div>
              <div
                className="flex-1 p-2 space-y-2 overflow-y-auto transition-colors"
                style={{
                  backgroundColor: dragOverCol === s.value ? `${s.color}11` : undefined,
                  minHeight: 100,
                }}
              >
                {grouped[s.value]?.map((lead) => (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, lead)}
                    onDragEnd={onDragEnd}
                    onClick={() => openEdit(lead)}
                    className="bg-[var(--bg)] border border-[var(--border)] rounded-lg p-3 cursor-pointer hover:border-[var(--accent)] transition-all"
                    style={{ opacity: draggingId === lead.id ? 0.4 : 1 }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm leading-tight">{lead.name || "Unnamed"}</div>
                      <GripVertical size={14} className="text-[var(--text-muted)] flex-shrink-0 mt-0.5 cursor-grab" />
                    </div>
                    {lead.company && (
                      <div className="text-xs text-[var(--text-muted)] mt-1">{lead.company}</div>
                    )}
                    {importanceStars(lead.notes)}
                    {lead.lastContacted && (
                      <div className="text-xs text-[var(--text-muted)] mt-1.5">Last: {lead.lastContacted}</div>
                    )}
                    {lead.notes && (
                      <div className="text-xs text-[var(--text-muted)] mt-1.5 line-clamp-2">{lead.notes.slice(0, 100)}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <Modal title="Add Lead" onClose={() => setShowCreate(false)}>
          <LeadForm lead={createForm as LeadItem} onChange={(field, value) => setCreateForm((p) => ({ ...p, [field]: value }))} />
          <div className="flex justify-end gap-2 mt-4">
            <Btn variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Btn>
            <Btn onClick={createLead}>Create</Btn>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal title={`Lead #${editing.id}`} onClose={() => setEditing(null)} wide>
          <LeadForm lead={editing} onChange={(field, value) => setEditing((p) => (p ? { ...p, [field]: value } : p))} />
          <div className="mt-4 border-t border-[var(--border)] pt-4">
            <h4 className="font-semibold mb-2">Activity Log / Notes</h4>
            <div className="flex gap-2 mb-3">
              <input className="input-field" placeholder="Add timestamped note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} />
              <Btn onClick={addNote}>Add</Btn>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {notes.map((n) => (
                <div key={n.id} className="text-sm bg-[var(--bg-column)] border border-[var(--border)] rounded-lg px-3 py-2">
                  <div className="text-xs text-[var(--text-muted)] mb-1">{new Date(n.createdAt).toLocaleString()}</div>
                  <div>{n.text}</div>
                </div>
              ))}
              {!notes.length && <div className="text-sm text-[var(--text-muted)]">No activity yet.</div>}
            </div>
          </div>
          <div className="flex justify-between mt-4">
            <Btn variant="danger" onClick={async () => { await leadStore.remove(editing.id); setEditing(null); await loadLeads(); }}><Trash2 size={14} /> Delete</Btn>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={() => setEditing(null)}><X size={14} /> Cancel</Btn>
              <Btn onClick={saveEdit}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LeadForm({ lead, onChange }: { lead: LeadItem; onChange: (field: keyof LeadItem, value: string) => void }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Name"><input value={lead.name || ""} onChange={(e) => onChange("name", e.target.value)} className="input-field" /></Field>
      <Field label="Title"><input value={lead.title || ""} onChange={(e) => onChange("title", e.target.value)} className="input-field" /></Field>
      <Field label="Company"><input value={lead.company || ""} onChange={(e) => onChange("company", e.target.value)} className="input-field" /></Field>
      <Field label="Website"><input value={lead.website || ""} onChange={(e) => onChange("website", e.target.value)} className="input-field" /></Field>
      <Field label="LinkedIn"><input value={lead.linkedin || ""} onChange={(e) => onChange("linkedin", e.target.value)} className="input-field" /></Field>
      <Field label="Email"><input value={lead.email || ""} onChange={(e) => onChange("email", e.target.value)} className="input-field" /></Field>
      <Field label="Phone"><input value={lead.phone || ""} onChange={(e) => onChange("phone", e.target.value)} className="input-field" /></Field>
      <Field label="City"><input value={lead.city || ""} onChange={(e) => onChange("city", e.target.value)} className="input-field" /></Field>
      <Field label="State"><input value={lead.state || ""} onChange={(e) => onChange("state", e.target.value)} className="input-field" /></Field>
      <Field label="Status">
        <select value={lead.status || "new"} onChange={(e) => onChange("status", e.target.value)} className="input-field">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </Field>
      <Field label="Last Contacted"><input value={lead.lastContacted || ""} onChange={(e) => onChange("lastContacted", e.target.value)} className="input-field" placeholder="YYYY-MM-DD" /></Field>
      <Field label="Notes"><textarea value={lead.notes || ""} onChange={(e) => onChange("notes", e.target.value)} className="input-field" rows={3} /></Field>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="text-xs text-[var(--text-muted)]">{label}<div className="mt-1">{children}</div></label>;
}

function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-5 w-full ${wide ? "max-w-4xl" : "max-w-xl"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Btn({ children, onClick, variant }: { children: React.ReactNode; onClick: () => void; variant?: "ghost" | "danger" }) {
  const cls = variant === "ghost" ? "text-[var(--text-muted)] hover:text-[var(--text)]" : variant === "danger" ? "text-[var(--red)] hover:bg-[var(--red)]/10" : "bg-[var(--accent)] hover:bg-[var(--accent-hover)] font-medium";
  return <button onClick={onClick} className={`px-3 py-2 rounded-lg text-sm transition-colors inline-flex items-center gap-1 ${cls}`}>{children}</button>;
}
